// Catch Mind (Draw & Guess) Game Manager

import { ChatPanel } from './chat_panel';

interface CatchMindState {
  status: string;
  roundDuration: number;
  currentRound: number;
  maxRounds: number;
  currentTurnIndex: number;
  turnOrder: number[];
  scores: Record<string, number>;
  currentDrawerId: number | null;
  currentWord?: string;
  wordLength: number;
  turnStartedAt: string | null;
  serverTime: string;
  startedAt: string | null;
  finishedAt: string | null;
}

type DrawingEnabledCallback = (enabled: boolean) => void;

export class CatchMindManager {
  private roomCode: string;
  private userId: number;
  private userName: string;
  private isHost: boolean;
  private onDrawingEnabledChange: DrawingEnabledCallback;

  private channel: any = null;
  private chatPanel: ChatPanel | null = null;
  private state: CatchMindState | null = null;
  private clockSkew: number = 0;
  private timerInterval: number | null = null;
  private advanceTimeout: number | null = null;
  private overlayEl: HTMLElement | null = null;
  private turnTransitionTimeout: number | null = null;
  private participantNames: Map<number, string> = new Map();
  private csrfToken: string;

  constructor(
    roomCode: string,
    userId: number,
    userName: string,
    isHost: boolean,
    onDrawingEnabledChange: DrawingEnabledCallback
  ) {
    this.roomCode = roomCode;
    this.userId = userId;
    this.userName = userName;
    this.isHost = isHost;
    this.onDrawingEnabledChange = onDrawingEnabledChange;
    this.overlayEl = document.getElementById('drawing-relay-overlay');
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  setChatPanel(chatPanel: ChatPanel): void {
    this.chatPanel = chatPanel;
  }

  bindToChannel(channel: any): void {
    if (!channel) return;
    this.channel = channel;

    channel.bind('catch-mind-started', (data: CatchMindState) => {
      this.onGameStarted(data);
    });

    channel.bind('catch-mind-turn-changed', (data: CatchMindState) => {
      this.onTurnChanged(data);
    });

    channel.bind('catch-mind-finished', (data: CatchMindState) => {
      this.onGameFinished(data);
    });

    channel.bind('catch-mind-correct-guess', (data: any) => {
      // Visual feedback for correct guess
    });

    // Collect participant names
    channel.bind('pusher:subscription_succeeded', (members: any) => {
      if (members && members.each) {
        members.each((member: any) => {
          if (member.info?.name) {
            this.participantNames.set(parseInt(member.id, 10), member.info.name);
          }
        });
      }
    });

    channel.bind('pusher:member_added', (member: any) => {
      if (member.info?.name) {
        this.participantNames.set(parseInt(member.id, 10), member.info.name);
      }
    });

    channel.bind('pusher:member_removed', (member: any) => {
      if (!this.state || this.state.status !== 'playing') return;
      const removedId = parseInt(member.id, 10);
      if (removedId === this.state.currentDrawerId && this.isHost) {
        this.postAdvance();
      }
    });
  }

  isActive(): boolean {
    return this.state?.status === 'playing';
  }

  async startGame(roundDuration: number, maxRounds: number): Promise<void> {
    try {
      const resp = await fetch(`/drawing/room/${this.roomCode}/catch_mind/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ round_duration: roundDuration, max_rounds: maxRounds })
      });
      const data = await resp.json();
      if (!data.success) {
        alert(data.error || '캐치마인드 시작 실패');
      }
    } catch (e) {
      console.error('[CatchMindManager] Start failed:', e);
    }
  }

  async stopGame(): Promise<void> {
    try {
      await fetch(`/drawing/room/${this.roomCode}/catch_mind/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      });
    } catch (e) {
      console.error('[CatchMindManager] Stop failed:', e);
    }
  }

  async sendGuess(text: string): Promise<void> {
    if (!this.state || this.state.status !== 'playing') return;
    if (this.state.currentDrawerId === this.userId) return; // Drawer can't guess

    try {
      const resp = await fetch(`/drawing/room/${this.roomCode}/catch_mind/guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ guess: text })
      });
      const data = await resp.json();

      if (data.result === 'close' && this.chatPanel) {
        this.chatPanel.showHint('거의 맞았어요! 비슷합니다!');
      }
    } catch (e) {
      console.error('[CatchMindManager] Guess failed:', e);
    }
  }

  private async postAdvance(): Promise<void> {
    try {
      await fetch(`/drawing/room/${this.roomCode}/catch_mind/advance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      });
    } catch (e) {
      console.error('[CatchMindManager] Advance failed:', e);
    }
  }

  private onGameStarted(data: CatchMindState): void {
    this.state = data;
    this.calculateClockSkew(data.serverTime);
    this.updateDrawingEnabled();
    this.startTimer();
    this.renderOverlay();
    this.showTurnTransition();
    this.setupGuessCallback();
  }

  private onTurnChanged(data: CatchMindState): void {
    this.state = data;
    this.calculateClockSkew(data.serverTime);
    this.updateDrawingEnabled();
    this.startTimer();
    this.renderOverlay();
    this.showTurnTransition();
    this.setupGuessCallback();
  }

  private onGameFinished(data: CatchMindState): void {
    this.state = data;
    this.stopTimer();
    this.onDrawingEnabledChange(true);
    this.renderScoreboard();
    this.teardownGuessCallback();

    setTimeout(() => {
      this.clearOverlay();
      this.setToolbarOpacity(1);
    }, 8000);
  }

  private setupGuessCallback(): void {
    if (this.chatPanel && this.state?.status === 'playing') {
      if (this.state.currentDrawerId === this.userId) {
        // Drawer shouldn't guess
        this.chatPanel.setGuessCallback(null);
      } else {
        this.chatPanel.setGuessCallback((text: string) => this.sendGuess(text));
      }
    }
  }

  private teardownGuessCallback(): void {
    if (this.chatPanel) {
      this.chatPanel.setGuessCallback(null);
    }
  }

  private calculateClockSkew(serverTimeStr: string): void {
    const serverTime = new Date(serverTimeStr).getTime();
    this.clockSkew = serverTime - Date.now();
  }

  private getAdjustedNow(): number {
    return Date.now() + this.clockSkew;
  }

  private updateDrawingEnabled(): void {
    if (!this.state || this.state.status !== 'playing') {
      this.onDrawingEnabledChange(true);
      this.setToolbarOpacity(1);
      return;
    }

    const isMyTurn = this.state.currentDrawerId === this.userId;
    this.onDrawingEnabledChange(isMyTurn);
    this.setToolbarOpacity(isMyTurn ? 1 : 0.3);
  }

  private setToolbarOpacity(opacity: number): void {
    const toolbar = document.getElementById('drawing-toolbar');
    const props = document.getElementById('drawing-properties');
    const colorPicker = document.getElementById('drawing-color-picker');
    [toolbar, props, colorPicker].forEach(el => {
      if (el) el.style.opacity = String(opacity);
    });
  }

  private startTimer(): void {
    this.stopTimer();

    this.timerInterval = window.setInterval(() => {
      this.updateTimerUI();
    }, 1000);

    this.scheduleAdvance();
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.advanceTimeout) {
      clearTimeout(this.advanceTimeout);
      this.advanceTimeout = null;
    }
  }

  private getTimeRemaining(): number {
    if (!this.state || !this.state.turnStartedAt) return 0;
    const turnStart = new Date(this.state.turnStartedAt).getTime();
    const elapsed = (this.getAdjustedNow() - turnStart) / 1000;
    return Math.max(0, this.state.roundDuration - elapsed);
  }

  private scheduleAdvance(): void {
    if (this.advanceTimeout) clearTimeout(this.advanceTimeout);

    const remaining = this.getTimeRemaining();
    const isCurrentDrawer = this.state?.currentDrawerId === this.userId;

    const delay = isCurrentDrawer ? remaining * 1000 : (remaining + 2) * 1000;

    this.advanceTimeout = window.setTimeout(() => {
      if (this.state?.status === 'playing') {
        this.postAdvance();
      }
    }, Math.max(0, delay));
  }

  private updateTimerUI(): void {
    const timerEl = document.getElementById('catchmind-timer-value');
    if (!timerEl) return;

    const remaining = Math.ceil(this.getTimeRemaining());
    timerEl.textContent = `${remaining}`;

    if (remaining <= 10) {
      timerEl.className = 'text-2xl font-black text-red-400 animate-pulse';
    } else if (remaining <= 30) {
      timerEl.className = 'text-2xl font-black text-yellow-400';
    } else {
      timerEl.className = 'text-2xl font-black text-green-400';
    }
  }

  private getParticipantName(userId: number): string {
    if (userId === this.userId) return this.userName;
    return this.participantNames.get(userId) || `User ${userId}`;
  }

  private getWordDisplay(): string {
    if (!this.state) return '';
    const isDrawer = this.state.currentDrawerId === this.userId;

    if (isDrawer && this.state.currentWord) {
      return `<span class="text-yellow-300 text-xl font-black tracking-widest">${this.state.currentWord}</span>`;
    }

    // Show underscores for guessers
    const len = this.state.wordLength || 0;
    const underscores = Array(len).fill('_').join(' ');
    return `<span class="text-gray-400 text-lg font-mono tracking-widest">${underscores}</span>`;
  }

  private renderOverlay(): void {
    if (!this.overlayEl || !this.state) return;

    const isMyTurn = this.state.currentDrawerId === this.userId;
    const currentDrawerName = this.state.currentDrawerId
      ? this.getParticipantName(this.state.currentDrawerId)
      : '?';
    const remaining = Math.ceil(this.getTimeRemaining());

    // Timer color
    let timerClass = 'text-2xl font-black text-green-400';
    if (remaining <= 10) timerClass = 'text-2xl font-black text-red-400 animate-pulse';
    else if (remaining <= 30) timerClass = 'text-2xl font-black text-yellow-400';

    // Scores display (top 3)
    const sortedScores = Object.entries(this.state.scores)
      .map(([uid, score]) => ({ uid: parseInt(uid, 10), score: score as number }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    const scoresHTML = sortedScores.map((s, i) => {
      const medals = ['', '', ''];
      const name = this.getParticipantName(s.uid);
      return `<span class="text-xs text-gray-300">${medals[i] || ''} ${name}: <span class="text-white font-bold">${s.score}</span></span>`;
    }).join('<span class="text-gray-600 mx-1">|</span>');

    this.overlayEl.innerHTML = `
      <div style="pointer-events: none;" class="fixed top-14 left-1/2 -translate-x-1/2 z-[10000]">
        <div class="bg-gray-900/95 backdrop-blur border border-purple-700/50 rounded-xl px-5 py-3 shadow-2xl text-center min-w-[300px]">
          <div class="flex items-center justify-between mb-2">
            <span class="text-gray-400 text-xs">라운드 <span class="text-white font-bold">${this.state.currentRound}/${this.state.maxRounds}</span></span>
            <span id="catchmind-timer-value" class="${timerClass}">${remaining}</span>
            <span class="text-gray-400 text-xs">${isMyTurn ? '내 차례' : `${currentDrawerName} 그리는 중`}</span>
          </div>
          <div class="mb-2">${this.getWordDisplay()}</div>
          ${sortedScores.length > 0 ? `<div class="border-t border-gray-700 pt-1.5 mt-1.5">${scoresHTML}</div>` : ''}
        </div>
      </div>
      ${!isMyTurn ? `
        <div style="pointer-events: none;" class="fixed inset-0 z-[9999] border-4 border-violet-500/30 rounded-none"></div>
      ` : ''}
    `;
  }

  private showTurnTransition(): void {
    if (this.turnTransitionTimeout) clearTimeout(this.turnTransitionTimeout);
    if (!this.state || !this.overlayEl) return;

    const isMyTurn = this.state.currentDrawerId === this.userId;
    const message = isMyTurn ? '당신이 그릴 차례!' : `${this.getParticipantName(this.state.currentDrawerId!)} 차례`;
    const color = isMyTurn ? 'text-purple-300' : 'text-gray-300';

    const transitionEl = document.createElement('div');
    transitionEl.style.pointerEvents = 'none';
    transitionEl.className = 'fixed inset-0 z-[10001] flex items-center justify-center';
    transitionEl.innerHTML = `
      <div class="text-center">
        <div class="text-4xl font-black ${color} animate-bounce drop-shadow-2xl mb-2">${message}</div>
        ${isMyTurn && this.state.currentWord ?
          `<div class="text-2xl font-bold text-yellow-300 animate-pulse">제시어: ${this.state.currentWord}</div>` :
          `<div class="text-lg text-gray-400">단어를 맞춰보세요!</div>`
        }
      </div>
    `;

    this.overlayEl.appendChild(transitionEl);

    this.turnTransitionTimeout = window.setTimeout(() => {
      transitionEl.remove();
    }, 2500);
  }

  private renderScoreboard(): void {
    if (!this.overlayEl || !this.state) return;

    const sortedScores = Object.entries(this.state.scores)
      .map(([uid, score]) => ({ uid: parseInt(uid, 10), score: score as number }))
      .sort((a, b) => b.score - a.score);

    const medals = ['🥇', '🥈', '🥉'];

    const scoresHTML = sortedScores.map((s, i) => {
      const name = this.getParticipantName(s.uid);
      const medal = medals[i] || `${i + 1}.`;
      const isMe = s.uid === this.userId;
      return `
        <div class="flex items-center justify-between py-2 px-4 ${isMe ? 'bg-purple-900/40 rounded-lg' : ''}">
          <div class="flex items-center space-x-2">
            <span class="text-xl">${medal}</span>
            <span class="${isMe ? 'text-purple-300 font-bold' : 'text-white'} text-sm">${name}</span>
          </div>
          <span class="text-yellow-400 font-black text-lg">${s.score}점</span>
        </div>
      `;
    }).join('');

    this.overlayEl.innerHTML = `
      <div class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
        <div class="bg-gray-900 border border-purple-700/50 rounded-2xl p-6 shadow-2xl min-w-[320px] max-w-[400px]" style="pointer-events: auto;">
          <div class="text-center mb-4">
            <div class="text-3xl mb-1">🎉</div>
            <div class="text-white text-xl font-black">게임 종료!</div>
          </div>
          <div class="space-y-1 mb-4">${scoresHTML || '<div class="text-gray-400 text-sm text-center">점수 없음</div>'}</div>
          <button id="catchmind-scoreboard-close" class="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors text-sm">
            닫기
          </button>
        </div>
      </div>
    `;

    document.getElementById('catchmind-scoreboard-close')?.addEventListener('click', () => {
      this.clearOverlay();
      this.setToolbarOpacity(1);
    });
  }

  private clearOverlay(): void {
    if (this.overlayEl) {
      this.overlayEl.innerHTML = '';
    }
  }

  destroy(): void {
    this.stopTimer();
    if (this.turnTransitionTimeout) clearTimeout(this.turnTransitionTimeout);
    this.clearOverlay();
    this.teardownGuessCallback();

    if (this.channel) {
      this.channel.unbind('catch-mind-started');
      this.channel.unbind('catch-mind-turn-changed');
      this.channel.unbind('catch-mind-finished');
      this.channel.unbind('catch-mind-correct-guess');
    }
  }
}
